import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Mail, Phone, Globe, Download, Activity, MapPin, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const navLinks = [
    { href: '#emergency', label: 'Emergency Coordination' },
    { href: '#ai-doctor', label: 'AI Doctor' },
    { href: '/about', label: 'About', isRoute: true },
    { href: '#contact', label: 'Contact' }
  ];

  return (
    <footer className="relative bg-gradient-to-b from-blue-50/30 to-white py-16 sm:py-24 lg:py-32 px-4 sm:px-6 overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 via-transparent to-blue-50/10"></div>
      
      {/* Floating 3D Elements - Minimal & Subtle */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        {/* Heartbeat Line */}
        <motion.div
          className="absolute top-1/4 left-16"
          animate={{ 
            y: [0, -12, 0],
            rotateY: [0, 8, 0]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <Activity className="w-6 h-6 text-blue-300/80" strokeWidth={1.5} />
        </motion.div>

        {/* AI Nodes - Floating */}
        <motion.div
          className="absolute top-1/3 right-24"
          animate={{ 
            scale: [1, 1.15, 1],
            y: [0, -8, 0],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-3 h-3 bg-blue-400/60 rounded-full blur-[0.5px]" />
        </motion.div>

        <motion.div
          className="absolute bottom-1/3 right-32"
          animate={{ 
            scale: [1, 1.25, 1],
            opacity: [0.3, 0.6, 0.3],
            rotate: [0, 10, 0]
          }}
          transition={{ duration: 4, repeat: Infinity, delay: 2, ease: "easeInOut" }}
        >
          <div className="w-2 h-2 bg-blue-500/50 rounded-full blur-[0.5px]" />
        </motion.div>

        {/* Hospital Map Pin Outline */}
        <motion.div
          className="absolute bottom-1/4 left-20"
          animate={{ 
            y: [0, -10, 0],
            rotateY: [0, -12, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        >
          <MapPin className="w-7 h-7 text-blue-300/60" strokeWidth={1.5} />
        </motion.div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Main Footer Content - Centered Layout with 80% Whitespace */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 lg:gap-16 mb-12 sm:mb-16 lg:mb-20"
        >
          {/* Company Legal Identity */}
          <motion.div
            whileHover={{ y: -8, scale: 1.02 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3 mb-8">
              <motion.div
                whileHover={{ rotate: 5, scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <img 
                  src="/supaaid.jpeg" 
                  alt="SupaAid Logo" 
                  className="w-12 h-12 object-contain rounded-lg"
                />
              </motion.div>
              <h3 className="text-2xl font-bold text-gray-900 tracking-tight">SupaAid</h3>
            </div>
            
            <div className="space-y-3">
              <p className="text-base font-semibold text-gray-900">SupaAid HealthTech Pvt Ltd</p>
              <p className="text-sm text-gray-600 font-mono">CIN: U62099UP2025PTC238295</p>
            </div>

            {/* DPIIT Badge - removed per request */}
            {/*
            <motion.div
              whileHover={{ scale: 1.05, rotateY: 8, z: 20 }}
              className="inline-block mt-6"
              style={{ transformStyle: "preserve-3d" }}
            >
              <div className="bg-white/70 backdrop-blur-xl border border-white/40 shadow-lg rounded-2xl px-5 py-3 flex items-center gap-3">
                <img 
                  src="/DPIIT-header.png" 
                  alt="DPIIT Logo" 
                  className="w-20 h-20 object-contain"
                />
                <span className="text-sm font-semibold text-gray-800">DPIIT Recognized</span>
              </div>
            </motion.div>
            */}
          </motion.div>

          {/* Registered Office & Contact Combined */}
          <motion.div
            whileHover={{ y: -8, scale: 1.02 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="space-y-8"
          >
            {/* Registered Office */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4">Registered Office</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Plot No. 278, Bhikhapur Rd,<br />
                Haveli Awadh, Ayodhya,<br />
                Faizabad, UP – 224001
              </p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4">Contact</h4>
              <div className="space-y-3">
                <motion.a
                  href="tel:+918875024908"
                  whileHover={{ x: 4, scale: 1.02 }}
                  className="flex items-center gap-3 text-sm text-gray-600 hover:text-blue-600 transition-all duration-200 group"
                >
                  <Phone className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
                  +91 8875024908
                </motion.a>
                <motion.a
                  href="mailto:contact@supaaid.in"
                  whileHover={{ x: 4, scale: 1.02 }}
                  className="flex items-center gap-3 text-sm text-gray-600 hover:text-blue-600 transition-all duration-200 group"
                >
                  <Mail className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
                  contact@supaaid.in
                </motion.a>
                <motion.a
                  href="https://www.supaaid.in"
                  whileHover={{ x: 4, scale: 1.02 }}
                  className="flex items-center gap-3 text-sm text-gray-600 hover:text-blue-600 transition-all duration-200 group"
                >
                  <Globe className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
                  www.supaaid.in
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Navigation & Download App */}
          <motion.div
            whileHover={{ y: -8, scale: 1.02 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="space-y-8"
          >
            {/* Navigation Links */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4">Navigation</h4>
              <div className="flex flex-wrap gap-2 text-sm">
                {navLinks.map((link, index) => (
                  <React.Fragment key={index}>
                    {link.isRoute ? (
                      <Link
                        to={link.href}
                        className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <motion.a
                        href={link.href}
                        whileHover={{ scale: 1.05 }}
                        className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
                      >
                        {link.label}
                      </motion.a>
                    )}
                    {index < navLinks.length - 1 && <span className="text-gray-300">•</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Download App Card - Frosted Glass */}
            <motion.div
              whileHover={{ scale: 1.03, rotateY: 5, z: 30 }}
              className="bg-white/60 backdrop-blur-xl border-2 border-dashed border-blue-200/70 rounded-2xl p-6 shadow-xl"
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.div
                animate={{ 
                  rotate: [0, 3, -3, 0],
                  y: [0, -3, 0]
                }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="flex justify-center mb-4"
              >
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-full p-4 shadow-lg">
                  <Download className="w-6 h-6 text-white" />
                </div>
              </motion.div>
              
              <h5 className="text-center font-bold text-gray-900 mb-2">SupaDoc App</h5>
              <p className="text-xs text-center text-gray-600 mb-4">
                Coming Soon
              </p>
              
              <div className="bg-blue-50/80 backdrop-blur-sm border border-blue-100 rounded-xl px-4 py-3 text-center">
                <span className="text-xs font-medium text-gray-700">Coming Soon</span>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Elegant Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent mb-12"
        />

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="space-y-6"
        >
          {/* Medical AI Disclaimer */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50/60 backdrop-blur-sm border border-blue-100/70 rounded-2xl px-8 py-5 text-center">
              <p className="text-xs text-gray-600 leading-relaxed">
                <span className="font-semibold text-gray-800">Medical AI Disclaimer:</span> AI assistance only. For confirmed diagnosis, consult licensed professionals.
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              © 2025 <span className="font-semibold text-gray-900">SupaAid Healthtech Private Limited</span>. All Rights Reserved.
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
